import React from 'react';
import { Alert, AlertTitle, Box, Button, Stack, Typography } from '@mui/material';
import { AccessTime } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { hasLatePaymentRefundNotice } from '../../utils/orderHelpers';
import { useReorderExpired } from '../../hooks/customer/useReorderExpired';

/**
 * Sprint B — banner shown on cancelled orders that were auto-cancelled by
 * the pending-orders cron (i.e. customer didn't pay within 30 min). It
 * differentiates the cancellation cause from a customer/admin cancel and
 * offers a one-click "Reorder" CTA. If a late payment landed after the
 * sweep, a small refund-eta line is appended (admin issues the refund —
 * frontend just informs the customer).
 */
const ExpiredOrderBanner = ({ order }) => {
  const { t } = useTranslation();
  const { reorder, isReordering } = useReorderExpired();
  const showLatePaymentNote = hasLatePaymentRefundNotice(order);

  return (
    <Alert
      severity="warning"
      icon={<AccessTime fontSize="inherit" />}
      sx={{ mb: 2, borderRadius: 2, alignItems: 'flex-start' }}
    >
      <AlertTitle sx={{ fontWeight: 700 }}>
        {t('orders.expired.title', 'Order expired')}
      </AlertTitle>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t(
          'orders.expired.body',
          "This order was cancelled because payment wasn't completed within 30 minutes."
        )}
      </Typography>
      {showLatePaymentNote && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {t(
            'orders.expired.latePaymentNote',
            'If you completed the payment, your refund will arrive within 5 business days.'
          )}
        </Typography>
      )}
      <Box sx={{ mt: 1 }}>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="warning"
            disabled={isReordering || !order?.items?.length}
            onClick={() => reorder(order)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {isReordering
              ? t('common.loading', 'Loading...')
              : t('orders.expired.reorder', 'Reorder these items')}
          </Button>
        </Stack>
      </Box>
    </Alert>
  );
};

export default ExpiredOrderBanner;
